import { type Cell, type Direction, type Transition } from '@2k48/shared';
import { BoardRenderer, type EntitySnapshot } from './BoardRenderer';
import { EffectsLayer } from './EffectsLayer';
import { InputController } from './InputController';
import { applyMotionTokens, createMotionScope, type MotionScope } from './motion';

/**
 * Mounts the imperative play area into a container and owns its lifetime.
 *
 * This is the seam between Svelte and the game. Svelte renders the screen the
 * board sits on — the HUD, the buttons, the result panel — and then hands over
 * one empty `<div>`. Everything inside it is managed here, by hand.
 *
 * That split is deliberate. A framework decides *when* to re-render, based on
 * what it thinks changed. At 60fps with a dozen simultaneously animating nodes,
 * that decision has to be ours: which nodes move, in what order, on which
 * frame, reusing which elements. A reactive diff cannot express "this specific
 * node travels from here to there over 110ms while that one pops".
 *
 * Teardown matters as much as setup: `destroy()` reverts the GSAP context, so
 * no tween outlives the DOM it was animating.
 */

export interface GameAreaOptions {
  cols: number;
  rows: number;
  defaultTone?: string;
  onDirection?(direction: Direction): void;
  onTap?(cell: Cell | null): void;
  onSettled?(): void;
}

export interface GameAreaHandle {
  readonly element: HTMLElement;
  readonly renderer: BoardRenderer;
  readonly effects: EffectsLayer;
  readonly input: InputController;
  /** Animate a batch, routing `x:` transitions to the effects layer. */
  apply(transitions: readonly Transition[]): ReturnType<BoardRenderer['apply']>;
  /** Hard resync, no animation. */
  sync(entities: readonly EntitySnapshot[]): void;
  setGrid(cols: number, rows: number): void;
  destroy(): void;
}

export function mountGameArea(container: HTMLElement, options: GameAreaOptions): GameAreaHandle {
  applyMotionTokens();

  const element = document.createElement('div');
  element.className = 'game-area';
  container.appendChild(element);

  const scope: MotionScope = createMotionScope(element);

  const renderer = new BoardRenderer(element, {
    cols: options.cols,
    rows: options.rows,
    ...(options.defaultTone !== undefined ? { defaultTone: options.defaultTone } : {}),
    ...(options.onSettled ? { onSettled: options.onSettled } : {}),
  });

  const effects = new EffectsLayer(renderer.root, {
    positionOf: (cell) => renderer.positionOf(cell),
  });

  const input = new InputController({
    target: renderer.root,
    cellAt: (point) => cellAt(renderer, point),
    handlers: {
      onDirection: (direction) => options.onDirection?.(direction),
      ...(options.onTap ? { onTap: (cell: Cell | null) => options.onTap?.(cell) } : {}),
    },
  });

  return {
    element,
    renderer,
    effects,
    input,

    apply(transitions) {
      // Board transitions animate; ruleset-defined `x:` ones fire as effects.
      // Both come from the same batch so they stay in step.
      let timeline: ReturnType<BoardRenderer['apply']> | undefined;
      scope.add(() => {
        timeline = renderer.apply(transitions);
        effects.playTransitions(transitions);
      });
      if (!timeline) throw new Error('Motion scope did not create the board timeline.');
      return timeline;
    },

    sync(entities) {
      renderer.sync(entities);
    },

    setGrid(cols, rows) {
      renderer.setGrid(cols, rows);
    },

    destroy() {
      input.destroy();
      effects.destroy();
      renderer.destroy();
      // Kills every tween created inside this scope, including any still
      // running, and reverts the inline styles they set. Without it, tweens
      // keep ticking against detached nodes and the client degrades a little
      // with every screen the player visits.
      scope.destroy();
      element.remove();
    },
  };
}

/**
 * Which cell a viewport point falls in, or null if it is outside the grid.
 *
 * This one does read layout, and that is fine: it runs once per pointer press,
 * not once per frame, and it needs the layer's position in the viewport, which
 * is the one thing the renderer's cached board-space geometry cannot supply.
 */
function cellAt(renderer: BoardRenderer, point: { x: number; y: number }): Cell | null {
  const layer = renderer.root.querySelector<HTMLElement>('.board__cells');
  const sample = layer?.firstElementChild as HTMLElement | null;
  if (!layer || !sample) return null;

  const rect = layer.getBoundingClientRect();
  const size = sample.offsetWidth;
  const cols = Number(renderer.root.style.getPropertyValue('--cols')) || 1;
  const rows = Number(renderer.root.style.getPropertyValue('--rows')) || 1;
  const step = cols > 1 ? (rect.width - size) / (cols - 1) : size;
  if (step <= 0) return null;

  const col = Math.floor((point.x - rect.left) / step);
  const row = Math.floor((point.y - rect.top) / step);

  if (col < 0 || row < 0 || col >= cols || row >= rows) return null;
  return { col, row };
}

export type { EntitySnapshot };
